/**
 * StepIndicator Component
 *
 * A reusable step indicator component for multi-step workflows.
 * Supports horizontal and vertical layouts with theme-aware styling.
 *
 * @module components/ui/StepIndicator/StepIndicator
 */

import React from 'react';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';
import type { StepIndicatorProps, StepStatusProps } from './types';
import {
  getStepStatus,
  getStepCircleClasses,
  getStepIconClasses,
  getConnectorClasses,
  getTextSizeClasses,
  getContainerClasses,
  getStepsListClasses
} from './utils';

/**
 * Individual step component
 */
const StepItem: React.FC<StepStatusProps> = ({
  step,
  status,
  isLast,
  onClick,
  size,
  variant
}) => {
  const { isDark } = useTheme();
  const isCompleted = status === 'completed';
  const isCurrent = status === 'current';
  const isClickable = onClick && (isCompleted || isCurrent);

  const circleClasses = getStepCircleClasses(size);
  const iconClasses = getStepIconClasses(size);
  const connectorClasses = getConnectorClasses(size, variant);
  const textSizes = getTextSizeClasses(size);

  const handleClick = () => {
    if (isClickable) {
      onClick(step.key);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(step.key);
    }
  };

  return (
    <li className={cn(
      'flex items-center',
      variant === 'vertical' ? 'flex-col' : 'flex-row'
    )}>
      {/* Step Circle and Content */}
      <div className={cn(
        'flex items-center text-center',
        variant === 'vertical' ? 'flex-col text-center' : 'flex-row'
      )}>
        {/* Step Circle */}
        <div
          className={cn(
            circleClasses,
            isCompleted && 'bg-primary-600 border-primary-600 text-white',
            isCurrent && (isDark ? 'bg-primary-900 text-primary-300 border-primary-600' : 'bg-primary-50 text-primary-600 border-primary-600'),
            status === 'upcoming' && (isDark ? 'bg-neutral-800 border-neutral-600 text-neutral-500' : 'bg-neutral-100 border-neutral-600 text-neutral-600'),
            isClickable && 'cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
          )}
          aria-current={isCurrent ? 'step' : undefined}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={isClickable ? 0 : undefined}
          role={isClickable ? 'button' : undefined}
          aria-label={isClickable ? `Go to ${step.title}` : undefined}
        >
          {isCompleted ? (
            <svg className={iconClasses} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <div className={iconClasses}>
              {step.icon}
            </div>
          )}
        </div>

        {/* Step Label */}
        <div className={cn(
          variant === 'vertical' ? 'mt-2' : 'ml-2 hidden sm:block'
        )}>
          <p
            className={cn(
              textSizes.title,
              isCurrent && (isDark ? 'text-primary-400' : 'text-primary-600'),
              isCompleted && (isDark ? 'text-neutral-100' : 'text-neutral-900'),
              status === 'upcoming' && (isDark ? 'text-neutral-400' : 'text-neutral-600')
            )}
          >
            {step.title}
          </p>
          {step.description && (
            <p className={cn(
              textSizes.description,
              isDark ? "text-neutral-400" : "text-neutral-600"
            )}>
              {step.description}
            </p>
          )}
        </div>
      </div>

      {/* Connector Line */}
      {!isLast && (
        <div
          className={cn(
            connectorClasses,
            variant === 'vertical' ? 'mt-2 mb-2' : 'ml-2 sm:ml-4',
            isCompleted && 'bg-primary-600',
            !isCompleted && (isDark ? 'bg-neutral-600' : 'bg-neutral-300')
          )}
          aria-hidden="true"
        />
      )}
    </li>
  );
};

/**
 * StepIndicator component for displaying multi-step workflow progress
 *
 * @example
 * ```tsx
 * const steps = [
 *   { key: 'step1', title: 'Step 1', description: 'First step', icon: <Icon1 /> },
 *   { key: 'step2', title: 'Step 2', description: 'Second step', icon: <Icon2 /> }
 * ];
 *
 * <StepIndicator
 *   steps={steps}
 *   currentStep="step1"
 *   onStepClick={(stepKey) => navigateToStep(stepKey)}
 *   variant="horizontal"
 *   size="md"
 * />
 * ```
 */
export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  onStepClick,
  className,
  variant = 'horizontal',
  size = 'md',
  ariaLabel = 'Progress indicator',
  statusOverrides
}) => {
  const containerClasses = getContainerClasses(variant);
  const stepsListClasses = getStepsListClasses(variant, size);

  return (
    <div className={cn(containerClasses, className)}>
      <nav aria-label={ariaLabel}>
        <ol className={stepsListClasses}>
          {steps.map((step, index) => {
            const status = statusOverrides?.[step.key] ?? getStepStatus(step.key, currentStep, steps);
            const isLast = index === steps.length - 1;

            return (
              <StepItem
                key={step.key}
                step={step}
                status={status}
                isLast={isLast}
                onClick={onStepClick}
                size={size}
                variant={variant}
              />
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default StepIndicator;